import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft, 
  Plus, 
  Pencil, 
  Trash2, 
  MapPin, 
  Clock, 
  Building2,
  Users,
  Briefcase,
  Mail,
  Phone,
  FileText,
  Calendar,
  Eye,
  Lock,
  LogOut,
  Linkedin,
  Globe,
  Github,
  Twitter,
  Loader2,
  Download
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  verifyAdminPassword, 
  clearAdminCredentials,
  getJobs, 
  createJob, 
  updateJob, 
  deleteJob,
  getApplications,
  updateApplicationStatus,
  setAdminCredentials,
  downloadResume
} from '../services/api';

const FRDM_LOGO = "https://customer-assets.emergentagent.com/job_site-reveal-2/artifacts/ci7bb29v_FRDM.png";

const defaultFieldReqs = {
  phone: false,
  resume: false,
  coverLetter: false,
  linkedin: false,
  portfolio: false,
  github: false,
  twitter: false
};

const Admin = () => {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  // Data state
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);

  const [isJobModalOpen, setIsJobModalOpen] = useState(false);
  const [isApplicationModalOpen, setIsApplicationModalOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [filterJobId, setFilterJobId] = useState('all');
  const [savingJob, setSavingJob] = useState(false);
  
  const [jobForm, setJobForm] = useState({
    title: '',
    location: '',
    type: 'Full-time',
    department: '',
    description: '',
    requirements: '',
    status: 'active',
    fieldRequirements: { ...defaultFieldReqs }
  });

  // Check for existing session
  useEffect(() => {
    const savedAuth = sessionStorage.getItem('frdm_admin_auth');
    const savedPassword = sessionStorage.getItem('frdm_admin_pwd');
    if (savedAuth === 'true' && savedPassword) {
      setAdminCredentials(savedPassword);
      setIsAuthenticated(true);
    }
  }, []);

  // Load data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [jobsData, appsData] = await Promise.all([
        getJobs(),
        getApplications()
      ]);
      setJobs(jobsData);
      setApplications(appsData);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    
    try {
      const success = await verifyAdminPassword(password);
      if (success) {
        setIsAuthenticated(true);
        sessionStorage.setItem('frdm_admin_auth', 'true');
        sessionStorage.setItem('frdm_admin_pwd', password);
        toast.success('Welcome to the admin dashboard');
      } else {
        setAuthError('Incorrect password');
        toast.error('Incorrect password');
      }
    } catch (error) {
      setAuthError('Authentication failed');
      toast.error('Authentication failed');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    clearAdminCredentials();
    sessionStorage.removeItem('frdm_admin_auth');
    sessionStorage.removeItem('frdm_admin_pwd');
    setPassword('');
    setJobs([]);
    setApplications([]);
    toast.success('Logged out successfully');
  };

  const handleOpenJobModal = (job = null) => {
    if (job) {
      setEditingJob(job);
      setJobForm({
        title: job.title,
        location: job.location,
        type: job.type,
        department: job.department,
        description: job.description,
        requirements: job.requirements,
        status: job.status,
        fieldRequirements: job.fieldRequirements || { ...defaultFieldReqs }
      });
    } else {
      setEditingJob(null);
      setJobForm({
        title: '',
        location: '',
        type: 'Full-time',
        department: '',
        description: '',
        requirements: '',
        status: 'active',
        fieldRequirements: { ...defaultFieldReqs }
      });
    }
    setIsJobModalOpen(true);
  };

  const handleSaveJob = async (e) => {
    e.preventDefault();
    
    if (!jobForm.title || !jobForm.location || !jobForm.department) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSavingJob(true);
    try {
      if (editingJob) {
        const updated = await updateJob(editingJob.id, jobForm);
        setJobs(jobs.map(j => j.id === editingJob.id ? updated : j));
        toast.success('Job updated successfully');
      } else {
        const newJob = await createJob(jobForm);
        setJobs([...jobs, newJob]);
        toast.success('Job created successfully');
      }
      setIsJobModalOpen(false);
    } catch (error) {
      toast.error('Failed to save job');
    } finally {
      setSavingJob(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (window.confirm('Are you sure you want to delete this job? All applications will also be removed.')) {
      try {
        await deleteJob(jobId);
        setJobs(jobs.filter(j => j.id !== jobId));
        setApplications(applications.filter(a => a.jobId !== jobId));
        toast.success('Job deleted');
      } catch (error) {
        toast.error('Failed to delete job');
      }
    }
  };

  const handleViewApplication = (application) => {
    setSelectedApplication(application);
    setIsApplicationModalOpen(true);
  };

  const handleUpdateApplicationStatus = async (appId, newStatus) => {
    try {
      await updateApplicationStatus(appId, newStatus);
      setApplications(applications.map(a => 
        a.id === appId ? { ...a, status: newStatus } : a
      ));
      if (selectedApplication?.id === appId) {
        setSelectedApplication({ ...selectedApplication, status: newStatus });
      }
      toast.success(`Application marked as ${newStatus}`);
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const filteredApplications = filterJobId === 'all' 
    ? applications 
    : applications.filter(a => a.jobId === filterJobId);

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-500/20 text-blue-400';
      case 'reviewed': return 'bg-amber-500/20 text-amber-400';
      case 'interviewed': return 'bg-purple-500/20 text-purple-400';
      case 'hired': return 'bg-green-500/20 text-green-400';
      case 'rejected': return 'bg-red-500/20 text-red-400';
      default: return 'bg-stone-500/20 text-stone-400';
    }
  };

  const getApplicationCount = (jobId) => {
    return applications.filter(a => a.jobId === jobId).length;
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center px-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img 
              src={FRDM_LOGO} 
              alt="FRDM" 
              className="h-12 mx-auto mb-6 invert"
            />
            <h1 className="text-2xl font-bold text-white mb-2">Admin Login</h1>
            <p className="text-stone-400">Enter the admin password to continue</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="password" className="text-stone-300">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-500" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  className="pl-10 bg-stone-800 border-stone-600 text-white placeholder:text-stone-500 focus:border-[#C41E3A]"
                  autoFocus
                />
              </div>
              {authError && (
                <p className="text-red-400 text-sm mt-2">{authError}</p>
              )}
            </div>
            
            <Button
              type="submit"
              disabled={authLoading}
              className="w-full bg-[#C41E3A] hover:bg-[#A01830] text-white font-semibold h-12"
            >
              {authLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sign In'}
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <Link 
              to="/" 
              className="text-stone-400 hover:text-white text-sm transition-colors"
            >
              ← Back to site
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#C41E3A] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-900">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black backdrop-blur-sm border-b border-stone-800">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link 
            to="/" 
            className="flex items-center gap-2 text-stone-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Site</span>
          </Link>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <img 
                src={FRDM_LOGO} 
                alt="FRDM" 
                className="h-8 invert"
              />
              <span className="text-stone-400">Admin</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-stone-400 hover:text-white hover:bg-stone-800"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        <Tabs defaultValue="jobs" className="w-full">
          <TabsList className="bg-stone-800 border border-stone-700 mb-8">
            <TabsTrigger 
              value="jobs" 
              className="data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white"
            >
              <Briefcase className="w-4 h-4 mr-2" />
              Jobs ({jobs.length})
            </TabsTrigger>
            <TabsTrigger 
              value="applications"
              className="data-[state=active]:bg-[#C41E3A] data-[state=active]:text-white"
            >
              <Users className="w-4 h-4 mr-2" />
              Applications ({applications.length})
            </TabsTrigger>
          </TabsList>

          {/* Jobs Tab */}
          <TabsContent value="jobs">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">Manage Jobs</h2>
              <Button 
                onClick={() => handleOpenJobModal()}
                className="bg-[#C41E3A] hover:bg-[#A01830] text-white font-semibold"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Job
              </Button>
            </div>

            <div className="space-y-4">
              {jobs.map((job) => (
                <div 
                  key={job.id}
                  className="bg-stone-800/50 border border-stone-700 rounded-xl p-6"
                >
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold text-white">{job.title}</h3>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          job.status === 'active' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-stone-500/20 text-stone-400'
                        }`}>
                          {job.status}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-stone-400">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {job.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {job.type}
                        </span>
                        <span className="flex items-center gap-1">
                          <Building2 className="w-4 h-4" />
                          {job.department}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {getApplicationCount(job.id)} applications
                        </span>
                      </div>
                      <p className="text-stone-400 mt-3 line-clamp-2">{job.description}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenJobModal(job)}
                        className="border-stone-600 text-stone-300 hover:bg-stone-700 hover:text-white"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteJob(job.id)}
                        className="border-red-800 text-red-400 hover:bg-red-900/50 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}

              {jobs.length === 0 && (
                <div className="text-center py-16 text-stone-400">
                  <Briefcase className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No jobs yet. Create your first job posting!</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
              <h2 className="text-2xl font-bold text-white">Applications</h2>
              <div className="flex items-center gap-2">
                <span className="text-stone-400 text-sm">Filter by job:</span>
                <Select value={filterJobId} onValueChange={setFilterJobId}>
                  <SelectTrigger className="w-48 bg-stone-800 border-stone-600 text-white">
                    <SelectValue placeholder="All Jobs" />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-stone-600">
                    <SelectItem value="all" className="text-white hover:bg-stone-700">All Jobs</SelectItem>
                    {jobs.map(job => (
                      <SelectItem key={job.id} value={job.id} className="text-white hover:bg-stone-700">
                        {job.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-4">
              {filteredApplications.map((app) => (
                <div 
                  key={app.id}
                  className="bg-stone-800/50 border border-stone-700 rounded-xl p-6 hover:border-stone-600 transition-colors"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-white">{app.name}</h3>
                        <span className={`text-xs px-2 py-1 rounded-full capitalize ${getStatusColor(app.status)}`}>
                          {app.status}
                        </span>
                      </div>
                      <p className="text-[#C41E3A] text-sm mb-2">Applied for: {app.jobTitle}</p>
                      <div className="flex flex-wrap gap-4 text-sm text-stone-400">
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {app.email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Phone className="w-4 h-4" />
                          {app.phone}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(app.appliedAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <Button
                      onClick={() => handleViewApplication(app)}
                      className="bg-stone-700 hover:bg-stone-600 text-white"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View Details
                    </Button>
                  </div>
                </div>
              ))}

              {filteredApplications.length === 0 && (
                <div className="text-center py-16 text-stone-400">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No applications yet.</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Job Modal */}
      <Dialog open={isJobModalOpen} onOpenChange={setIsJobModalOpen}>
        <DialogContent className="bg-stone-900 border-stone-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {editingJob ? 'Edit Job' : 'Add New Job'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSaveJob} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="title" className="text-stone-300">Job Title *</Label>
              <Input
                id="title"
                value={jobForm.title}
                onChange={(e) => setJobForm({...jobForm, title: e.target.value})}
                placeholder="e.g., Head Barista"
                className="mt-1 bg-stone-800 border-stone-600 text-white placeholder:text-stone-500"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="location" className="text-stone-300">Location *</Label>
                <Input
                  id="location"
                  value={jobForm.location}
                  onChange={(e) => setJobForm({...jobForm, location: e.target.value})}
                  placeholder="e.g., Philadelphia, PA"
                  className="mt-1 bg-stone-800 border-stone-600 text-white placeholder:text-stone-500"
                  required
                />
              </div>
              <div>
                <Label htmlFor="department" className="text-stone-300">Department *</Label>
                <Input
                  id="department"
                  value={jobForm.department}
                  onChange={(e) => setJobForm({...jobForm, department: e.target.value})}
                  placeholder="e.g., Operations"
                  className="mt-1 bg-stone-800 border-stone-600 text-white placeholder:text-stone-500"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-stone-300">Job Type</Label>
                <Select value={jobForm.type} onValueChange={(v) => setJobForm({...jobForm, type: v})}>
                  <SelectTrigger className="mt-1 bg-stone-800 border-stone-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-stone-600">
                    <SelectItem value="Full-time" className="text-white hover:bg-stone-700">Full-time</SelectItem>
                    <SelectItem value="Part-time" className="text-white hover:bg-stone-700">Part-time</SelectItem>
                    <SelectItem value="Contract" className="text-white hover:bg-stone-700">Contract</SelectItem>
                    <SelectItem value="Internship" className="text-white hover:bg-stone-700">Internship</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-stone-300">Status</Label>
                <Select value={jobForm.status} onValueChange={(v) => setJobForm({...jobForm, status: v})}>
                  <SelectTrigger className="mt-1 bg-stone-800 border-stone-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-stone-600">
                    <SelectItem value="active" className="text-white hover:bg-stone-700">Active</SelectItem>
                    <SelectItem value="paused" className="text-white hover:bg-stone-700">Paused</SelectItem>
                    <SelectItem value="closed" className="text-white hover:bg-stone-700">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="description" className="text-stone-300">Job Description</Label>
              <Textarea
                id="description"
                value={jobForm.description}
                onChange={(e) => setJobForm({...jobForm, description: e.target.value})}
                placeholder="Describe the role and responsibilities..."
                className="mt-1 bg-stone-800 border-stone-600 text-white placeholder:text-stone-500 min-h-[100px]"
              />
            </div>

            <div>
              <Label htmlFor="requirements" className="text-stone-300">Requirements (one per line)</Label>
              <Textarea
                id="requirements"
                value={jobForm.requirements}
                onChange={(e) => setJobForm({...jobForm, requirements: e.target.value})}
                placeholder="3+ years experience&#10;Leadership skills&#10;etc..."
                className="mt-1 bg-stone-800 border-stone-600 text-white placeholder:text-stone-500 min-h-[100px]"
              />
            </div>

            {/* Application Field Requirements */}
            <div className="pt-4 border-t border-stone-700">
              <Label className="text-stone-300 text-base font-semibold">Required Application Fields</Label>
              <p className="text-stone-500 text-xs mt-1 mb-4">Select which fields applicants must fill out (Name & Email are always required)</p>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="req-phone"
                    checked={jobForm.fieldRequirements?.phone || false}
                    onCheckedChange={(checked) => setJobForm({
                      ...jobForm,
                      fieldRequirements: { ...jobForm.fieldRequirements, phone: checked }
                    })}
                    className="border-stone-600 data-[state=checked]:bg-[#C41E3A] data-[state=checked]:border-[#C41E3A]"
                  />
                  <Label htmlFor="req-phone" className="text-stone-400 text-sm cursor-pointer">Phone</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="req-resume"
                    checked={jobForm.fieldRequirements?.resume || false}
                    onCheckedChange={(checked) => setJobForm({
                      ...jobForm,
                      fieldRequirements: { ...jobForm.fieldRequirements, resume: checked }
                    })}
                    className="border-stone-600 data-[state=checked]:bg-[#C41E3A] data-[state=checked]:border-[#C41E3A]"
                  />
                  <Label htmlFor="req-resume" className="text-stone-400 text-sm cursor-pointer">Resume/CV</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="req-coverLetter"
                    checked={jobForm.fieldRequirements?.coverLetter || false}
                    onCheckedChange={(checked) => setJobForm({
                      ...jobForm,
                      fieldRequirements: { ...jobForm.fieldRequirements, coverLetter: checked }
                    })}
                    className="border-stone-600 data-[state=checked]:bg-[#C41E3A] data-[state=checked]:border-[#C41E3A]"
                  />
                  <Label htmlFor="req-coverLetter" className="text-stone-400 text-sm cursor-pointer">Cover Letter</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="req-linkedin"
                    checked={jobForm.fieldRequirements?.linkedin || false}
                    onCheckedChange={(checked) => setJobForm({
                      ...jobForm,
                      fieldRequirements: { ...jobForm.fieldRequirements, linkedin: checked }
                    })}
                    className="border-stone-600 data-[state=checked]:bg-[#C41E3A] data-[state=checked]:border-[#C41E3A]"
                  />
                  <Label htmlFor="req-linkedin" className="text-stone-400 text-sm cursor-pointer flex items-center gap-1">
                    <Linkedin className="w-3 h-3" /> LinkedIn
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="req-portfolio"
                    checked={jobForm.fieldRequirements?.portfolio || false}
                    onCheckedChange={(checked) => setJobForm({
                      ...jobForm,
                      fieldRequirements: { ...jobForm.fieldRequirements, portfolio: checked }
                    })}
                    className="border-stone-600 data-[state=checked]:bg-[#C41E3A] data-[state=checked]:border-[#C41E3A]"
                  />
                  <Label htmlFor="req-portfolio" className="text-stone-400 text-sm cursor-pointer flex items-center gap-1">
                    <Globe className="w-3 h-3" /> Portfolio
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="req-github"
                    checked={jobForm.fieldRequirements?.github || false}
                    onCheckedChange={(checked) => setJobForm({
                      ...jobForm,
                      fieldRequirements: { ...jobForm.fieldRequirements, github: checked }
                    })}
                    className="border-stone-600 data-[state=checked]:bg-[#C41E3A] data-[state=checked]:border-[#C41E3A]"
                  />
                  <Label htmlFor="req-github" className="text-stone-400 text-sm cursor-pointer flex items-center gap-1">
                    <Github className="w-3 h-3" /> GitHub
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="req-twitter"
                    checked={jobForm.fieldRequirements?.twitter || false}
                    onCheckedChange={(checked) => setJobForm({
                      ...jobForm,
                      fieldRequirements: { ...jobForm.fieldRequirements, twitter: checked }
                    })}
                    className="border-stone-600 data-[state=checked]:bg-[#C41E3A] data-[state=checked]:border-[#C41E3A]"
                  />
                  <Label htmlFor="req-twitter" className="text-stone-400 text-sm cursor-pointer flex items-center gap-1">
                    <Twitter className="w-3 h-3" /> Twitter/X
                  </Label>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsJobModalOpen(false)}
                className="flex-1 border-stone-600 text-stone-300 hover:bg-stone-800 hover:text-white"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={savingJob}
                className="flex-1 bg-[#C41E3A] hover:bg-[#A01830] text-white font-semibold"
              >
                {savingJob ? <Loader2 className="w-5 h-5 animate-spin" /> : (editingJob ? 'Update Job' : 'Create Job')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Application Details Modal */}
      <Dialog open={isApplicationModalOpen} onOpenChange={setIsApplicationModalOpen}>
        <DialogContent className="bg-stone-900 border-stone-700 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              Application Details
            </DialogTitle>
          </DialogHeader>

          {selectedApplication && (
            <div className="mt-4 space-y-6">
              {/* Applicant Info */}
              <div className="bg-stone-800/50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-white mb-3">{selectedApplication.name}</h3>
                <div className="space-y-2 text-sm">
                  <p className="flex items-center gap-2 text-stone-300">
                    <Mail className="w-4 h-4 text-stone-500" />
                    {selectedApplication.email}
                  </p>
                  <p className="flex items-center gap-2 text-stone-300">
                    <Phone className="w-4 h-4 text-stone-500" />
                    {selectedApplication.phone}
                  </p>
                  <p className="flex items-center gap-2 text-stone-300">
                    <Briefcase className="w-4 h-4 text-stone-500" />
                    Applied for: <span className="text-[#C41E3A]">{selectedApplication.jobTitle}</span>
                  </p>
                  <p className="flex items-center gap-2 text-stone-300">
                    <Calendar className="w-4 h-4 text-stone-500" />
                    {new Date(selectedApplication.appliedAt).toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Social Links */}
              {(selectedApplication.linkedin || selectedApplication.portfolio || selectedApplication.github || selectedApplication.twitter) && (
                <div>
                  <h4 className="text-sm font-semibold text-stone-400 mb-2">Social Profiles</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {selectedApplication.linkedin && (
                      <a 
                        href={selectedApplication.linkedin} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 bg-stone-800 rounded-lg text-stone-300 hover:text-[#C41E3A] transition-colors text-sm"
                      >
                        <Linkedin className="w-4 h-4" />
                        LinkedIn
                      </a>
                    )}
                    {selectedApplication.portfolio && (
                      <a 
                        href={selectedApplication.portfolio} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 bg-stone-800 rounded-lg text-stone-300 hover:text-[#C41E3A] transition-colors text-sm"
                      >
                        <Globe className="w-4 h-4" />
                        Portfolio
                      </a>
                    )}
                    {selectedApplication.github && (
                      <a 
                        href={selectedApplication.github} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 bg-stone-800 rounded-lg text-stone-300 hover:text-[#C41E3A] transition-colors text-sm"
                      >
                        <Github className="w-4 h-4" />
                        GitHub
                      </a>
                    )}
                    {selectedApplication.twitter && (
                      <a 
                        href={selectedApplication.twitter} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 bg-stone-800 rounded-lg text-stone-300 hover:text-[#C41E3A] transition-colors text-sm"
                      >
                        <Twitter className="w-4 h-4" />
                        Twitter/X
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Resume */}
              <div>
                <h4 className="text-sm font-semibold text-stone-400 mb-2">Resume</h4>
                <div className="flex items-center justify-between gap-2 p-3 bg-stone-800 rounded-lg">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#C41E3A]" />
                    <span className="text-white">{selectedApplication.resumeFileName}</span>
                  </div>
                  {selectedApplication.resumeFilePath && selectedApplication.resumeFilePath !== "" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        try {
                          await downloadResume(selectedApplication.id, selectedApplication.resumeFileName);
                          toast.success('Resume downloaded');
                        } catch (error) {
                          toast.error('Failed to download resume');
                        }
                      }}
                      className="border-[#C41E3A] text-[#C41E3A] hover:bg-[#C41E3A] hover:text-white"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  )}
                </div>
              </div>

              {/* Cover Letter */}
              <div>
                <h4 className="text-sm font-semibold text-stone-400 mb-2">Cover Letter</h4>
                <p className="text-stone-300 text-sm bg-stone-800/50 p-4 rounded-lg">
                  {selectedApplication.coverLetter}
                </p>
              </div>

              {/* Status Update */}
              <div>
                <h4 className="text-sm font-semibold text-stone-400 mb-2">Update Status</h4>
                <div className="flex flex-wrap gap-2">
                  {['new', 'reviewed', 'interviewed', 'hired', 'rejected'].map((status) => (
                    <Button
                      key={status}
                      size="sm"
                      variant={selectedApplication.status === status ? 'default' : 'outline'}
                      onClick={() => handleUpdateApplicationStatus(selectedApplication.id, status)}
                      className={selectedApplication.status === status 
                        ? 'bg-[#C41E3A] text-white hover:bg-[#A01830]' 
                        : 'border-stone-600 text-stone-300 hover:bg-stone-700 capitalize'
                      }
                    >
                      {status}
                    </Button>
                  ))}
                </div>
              </div>

              <Button
                onClick={() => setIsApplicationModalOpen(false)}
                className="w-full bg-stone-700 hover:bg-stone-600 text-white"
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="border-t border-stone-800 py-8 mt-12">
        <p className="text-center text-stone-500 text-sm">
          © 2026 Freedom Coffee Inc. All rights reserved.
        </p>
      </footer>
    </div>
  );
};

export default Admin;
